import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { LucideChevronRight, LucideInfo } from '@lucide/angular';
import { DesignationChips } from '../designation-chips/designation-chips';
import {
  describeCadence,
  generalLineOf,
  NotificationEventSetting,
  notificationEventDescriptions,
  notificationEventLabels,
  notificationFamilies,
  particularLinesOf,
} from '../notification-setting.model';
import { NotificationSettingService } from '../notification-setting.service';

interface EventRow {
  setting: NotificationEventSetting;
  label: string;
  description: string | undefined;
  generalDesignations: NotificationEventSetting['lines'][number]['designations'];
  emptyLabel: string;
  particularCount: number;
  cadence: string | null;
}

/**
 * Réglages › Notifications : qui est prévenu de quoi, événement par événement, groupés par famille
 * (RG-notification-reglage-des-destinataires).
 */
@Component({
  selector: 'app-notification-settings',
  imports: [RouterLink, DesignationChips, LucideChevronRight, LucideInfo],
  templateUrl: './notification-settings.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-settings.scss'],
})
export class NotificationSettings {
  private readonly notificationSettingService = inject(NotificationSettingService);

  private readonly settings = toSignal(this.notificationSettingService.list(), {
    initialValue: [],
  });

  protected readonly groups = computed(() =>
    notificationFamilies
      .map(({ family, label }) => ({
        label,
        rows: this.settings()
          .filter((setting) => setting.family === family)
          .map((setting) => this.toRow(setting)),
      }))
      .filter((group) => group.rows.length > 0),
  );

  private toRow(setting: NotificationEventSetting): EventRow {
    const general = generalLineOf(setting);
    const particulars = particularLinesOf(setting);
    const nobody = setting.lines.every((line) => line.designations.length === 0);
    return {
      setting,
      label: notificationEventLabels[setting.event],
      description: notificationEventDescriptions[setting.event],
      generalDesignations: general?.designations ?? [],
      emptyLabel: nobody ? "Personne n'est notifié" : 'Aucun destinataire sur la ligne générale',
      particularCount: particulars.length,
      cadence: general?.cadence ? describeCadence(general.cadence) : null,
    };
  }
}
